import log from 'loglevel'
import {
  Box,
  Button,
  Dialog,
  DialogContent,
  DialogActions,
  Grid,
  MenuItem,
  Select,
  Typography,
  useMediaQuery,
  Snackbar,
  Alert,
  AlertTitle
} from '@mui/material'
import {
  Form,
  Labeled,
  Loading,
  SimpleShowLayout,
  TextField,
  useDataProvider,
  useGetOne,
  useInput,
  useTranslate
} from 'react-admin'
import { useParams } from 'react-router-dom'
import React, { useState, type SetStateAction } from 'react'
import { find, isNil, map, replace } from 'lodash/fp'
import { useFormContext, useFormState } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import * as z from 'zod'

import { type GmoBankAccount } from './types'
import { TextInput, ValidateError } from '../Util/index'
import { type EntryUserData } from '../../../../cdk/bapp/lib/lambda-api/src/types'
import { type UserAttribute } from '@mzc-watchover/adminapp-appsync'
import { useGetBankAccount } from 'components/UserDataProvider/hooks'

type SelectItem = {
  value: string
  name: string
}

log.setLevel(log.levels.DEBUG)
const env = process.env['REACT_APP_ENV'] as string

const schema = z.object({
  fields: z.object({
    bankCode: z
      .string({ message: 'required' })
      .min(4, { message: 'too small' })
      .max(4, { message: 'too large' }),
    branchCode: z
      .string({ message: 'required' })
      .min(3, { message: 'too small' })
      .max(3, { message: 'too large' }),
    accountType: z
      .string({ message: 'required' })
      .min(1, { message: 'required' }),
    accountNumber: z
      .string({ message: 'required' })
      .min(7, { message: 'too small' })
      .max(7, { message: 'too large' }),
    accountName: z
      .string({ message: 'required' })
      .min(1, { message: 'required' })
      .regex(/^[\u30A0-\u30FF]+$/, { message: 'must kana' }),
    accountNameKanji: z
      .string({ message: 'required' })
      .min(1, { message: 'required' })
  })
})
const SelectInput: React.FC<{
  source: string
  choices: SelectItem[]
}> = ({ source, choices, ...props }) => {
  const isDesktop = useMediaQuery((theme: any) => theme.breakpoints.up('md'))
  const { field, isRequired } = useInput({ source, ...props })
  const translate = useTranslate()
  const label = translate(`resources.orderR.${field.name}`)
  const { errors } = useFormState()
  return (
    <>
      <Typography variant="subtitle1">{label}</Typography>
      <Box
        {...props}
        border={0}
        sx={{
          height: '1.8em',
          display: 'flex',
          alignContent: 'center',
          marginTop: isDesktop ? '0.1em' : '0.25em',
          marginLeft: isDesktop ? '0.1em' : '0.25em',
          borderRadius: '10px',
          borderColor: '#c5c5c5'
        }}>
        <Select
          {...field}
          fullWidth
          sx={{
            marginTop: '0.2em',
            marginLeft: '0.5em',
            fontSize: '15px',
            borderRadius: '10px'
          }}
          required={isRequired}>
          {map((item: SelectItem) => (
            <MenuItem value={item.value}>
              {translate(`resources.orderR.${item.name}`)}
            </MenuItem>
          ))(choices)}
        </Select>
      </Box>
      {!isNil(errors[source]?.message) && (
        <ValidateError errorMessage={errors[source].message.toString()} />
      )}
    </>
  )
}
const BankAccountDialog: React.FC<
  GmoBankAccount & {
    open: boolean
  }
> = ({ TranID, Token, StartUrl, open }) => {
  const translate = useTranslate()
  // const [disable, setDisable] = React.useState(false)
  // const onSubmit = (): void => {
  //   log.debug('[BankAccountDialog]', { TranID, Token })
  //   setDisable(true)
  // }
  return (
    <Dialog open={open}>
      <form action={StartUrl} method="POST">
        <DialogContent>
          {translate('resources.orderR.bankAccount.dialog.content')}
        </DialogContent>
        <DialogActions>
          <Button
            type="submit"
            variant="contained"
            // disabled={disable}
            sx={{
              borderRadius: '40px',
              height: '3em'
            }}>
            <Typography>
              {translate('resources.orderR.bankAccount.dialog.ok')}
            </Typography>
          </Button>
        </DialogActions>
        <input type="hidden" name="TranID" value={TranID} />
        <input type="hidden" name="Token" value={replace(/ /g, '+')(Token)} />
      </form>
    </Dialog>
  )
}
const SubmitButton: React.FC<{
  id: string
  setGmoBankAccount: React.Dispatch<SetStateAction<GmoBankAccount | undefined>>
  setOpenAlert: React.Dispatch<SetStateAction<boolean>>
}> = ({ id, setGmoBankAccount, setOpenAlert }) => {
  const translate = useTranslate()
  const [enable, setEnable] = React.useState(false)
  const { getValues } = useFormContext()
  const { errors, isValid } = useFormState()
  const dataProvider = useDataProvider()
  const onClick = (): void => {
    setEnable(false)
    const { fields } = getValues()
    const data = {
      user: id,
      bankCode: fields.bankCode,
      branchCode: fields.branchCode,
      accountType: fields.accountType,
      accountNumber: fields.accountNumber,
      accountName: fields.accountName,
      accountNameKanji: fields.accountNameKanji
    }
    dataProvider
      .create('gmopg/bankaccount', { data })
      .then((result) => {
        setGmoBankAccount(result.data)
      })
      .catch((error) => {
        log.error(error)
        setEnable(true)
        setGmoBankAccount(undefined)
        setOpenAlert(true)
      })
  }
  React.useEffect(() => {
    setEnable(isValid)
  }, [errors, isValid, schema])
  return (
    <Button
      variant="contained"
      color="primary"
      onClick={onClick}
      disabled={!enable}
      sx={{
        borderRadius: '40px',
        height: '3em'
      }}>
      {translate('resources.orderR.bankAccount.submitButton')}
    </Button>
  )
}
const UserTextField: React.FC<{
  source: string
  label: boolean
  ownLabel?: string
}> = ({ source, ownLabel }) => {
  return (
    <Labeled
      label={
        !isNil(ownLabel) ? ownLabel : `resources.users.parsonalFields.${source}`
      }
      sx={{ fontVariant: 'body1' }}>
      <TextField variant="body1" source={source} />
    </Labeled>
  )
}
const BackAccountFormContent: React.FC<{
  id: string
  setGmoBankAccount: React.Dispatch<SetStateAction<GmoBankAccount | undefined>>
}> = ({ id, setGmoBankAccount }) => {
  const translate = useTranslate()
  const isDesktop = useMediaQuery((theme: any) => theme.breakpoints.up('md'))
  const { data: user, isLoading: isLoadingUser } = useGetOne<
    EntryUserData & { id: string }
  >('users', {
    id
  })
  const [openAlert, setOpenAlert] = useState<boolean>(false)
  const handleAlertClose = (): void => {
    setOpenAlert(false)
  }
  if (isLoadingUser) return <Loading />
  if (isNil(user)) return <></>
  const { attributes } = user
  const gmoMemberItem =
    env === 'exp'
      ? { key: 'gmoMemberId', value: 'KK_complete' }
      : find((item: UserAttribute) => item.key === 'gmoMemberId')(attributes)
  if (isNil(gmoMemberItem)) {
    log.error(`gmo item is not exist`)
    return <></>
  }
  const { isLoading, data: gmoPg } = useGetBankAccount(gmoMemberItem.value)
  if (isLoading) return <Loading />
  return (
    <Box sx={{ display: 'flex', justifyContent: 'center' }}>
      <Box sx={{ width: '1280px' }}>
        <Box sx={{ marginLeft: '0.5em' }}>
          <picture>
            <img src="/siteLogoR.svg" width="323px" alt="terashite R" />
          </picture>
        </Box>

        <Box sx={{ backgroundColor: '#fff7e1' }}>
          <Box sx={{ marginLeft: '0.5em', marginRight: '0.5em' }}>
            <Grid item xs={12} sx={{ marginTop: '2em' }}>
              <Grid container>
                <Grid item xs={12} display={'flex'} justifyContent={'center'}>
                  <Typography
                    variant="h5"
                    sx={{ marginTop: '1.5em', marginBottom: '1em' }}>
                    {'ご登録中の口座情報'}
                  </Typography>
                </Grid>
                <Grid
                  item
                  xs={12}
                  sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    mt: '-1em',
                    mb: '2em'
                  }}>
                  <picture>
                    <img src="/bar.png" alt="bar" />
                  </picture>
                </Grid>
              </Grid>
            </Grid>

            <Grid
              item
              xs={12}
              sx={{
                display: 'flex',
                justifyContent: 'center'
              }}>
              {!isNil(gmoPg) && (
                <SimpleShowLayout record={gmoPg}>
                  <UserTextField
                    source="BankCode"
                    label={false}
                    ownLabel="銀行コード"
                  />
                  {/* <UserTextField
                    source="BankName"
                    label={false}
                    ownLabel="銀行名"
                  /> */}
                  <UserTextField
                    source="BranchCode"
                    label={false}
                    ownLabel="支店コード"
                  />
                  {/* <UserTextField
                    source="BranchName"
                    label={false}
                    ownLabel="支店名"
                  /> */}
                  <UserTextField
                    source="AccountNumber"
                    label={false}
                    ownLabel="口座番号（下4桁）"
                  />
                  {/* <UserTextField
                    source="AccountName"
                    label={false}
                    ownLabel="口座名義"
                  /> */}
                </SimpleShowLayout>
              )}
            </Grid>

            <Grid item xs={12} sx={{ marginTop: '2em' }}>
              <Grid container>
                <Grid item xs={12} display={'flex'} justifyContent={'center'}>
                  <Typography
                    variant="h5"
                    sx={{ marginTop: '1.5em', marginBottom: '1em' }}>
                    {translate('resources.orderR.bankAccount.title')}
                  </Typography>
                </Grid>
                <Grid
                  item
                  xs={12}
                  sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    mt: '-1em',
                    mb: '2em'
                  }}>
                  <picture>
                    <img src="/bar.png" alt="bar" />
                  </picture>
                </Grid>
              </Grid>
            </Grid>

            <Grid item xs={12}>
              <Grid container>
                <Grid item xs={isDesktop ? 6 : 12}>
                  <TextInput
                    fields={'resources.orderR'}
                    source="fields.bankCode"
                    placeholder="0000"
                  />
                </Grid>
                <Grid item xs={isDesktop ? 6 : 12}>
                  <TextInput
                    fields={'resources.orderR'}
                    source="fields.branchCode"
                    placeholder="000"
                  />
                </Grid>
              </Grid>
            </Grid>
            <Grid item xs={12}>
              <Grid container>
                <Grid item xs={isDesktop ? 6 : 12}>
                  <SelectInput
                    source="fields.accountType"
                    choices={[
                      { value: '1', name: 'fields.savings' },
                      { value: '2', name: 'fields.current' }
                    ]}
                  />
                </Grid>
                <Grid item xs={isDesktop ? 6 : 12}>
                  <TextInput
                    fields={'resources.orderR'}
                    source="fields.accountNumber"
                    placeholder="0123456"
                  />
                </Grid>
              </Grid>
            </Grid>
            <Grid item xs={12}>
              <Grid container>
                <Grid item xs={isDesktop ? 6 : 12}>
                  <TextInput
                    fields={'resources.orderR'}
                    source="fields.accountName"
                    placeholder="チュウデンタロウ"
                  />
                </Grid>
                <Grid item xs={isDesktop ? 6 : 12}>
                  <TextInput
                    fields={'resources.orderR'}
                    source="fields.accountNameKanji"
                    placeholder="中電太郎"
                  />
                </Grid>
              </Grid>
            </Grid>
            <Snackbar
              open={openAlert}
              autoHideDuration={5000}
              onClose={handleAlertClose}>
              <Alert severity="error">
                <AlertTitle>{'口座登録エラー'}</AlertTitle>
                {'ご入力いただいた銀行コードもしくは支店コードは存在しません。'}
              </Alert>
            </Snackbar>
            <Grid
              item
              xs={12}
              sx={{
                display: 'flex',
                justifyContent: 'center',
                marginTop: '1.5em',
                paddingBottom: '2.5em'
              }}>
              <SubmitButton
                id={id}
                setGmoBankAccount={setGmoBankAccount}
                setOpenAlert={setOpenAlert}
              />
            </Grid>
          </Box>
        </Box>
      </Box>
    </Box>
  )
}

export const BankAccountForm: React.FC = () => {
  const params = useParams()
  const id = params['id'] as string
  const [gmoBankAccount, setGmoBankAccount] = React.useState<
    GmoBankAccount | undefined
  >(undefined)
  return !isNil(gmoBankAccount) ? (
    <BankAccountDialog
      TranID={gmoBankAccount.TranID}
      Token={gmoBankAccount.Token}
      StartUrl={gmoBankAccount.StartUrl}
      open
    />
  ) : (
    <Form resolver={zodResolver(schema)} mode="onBlur" reValidateMode="onBlur">
      <BackAccountFormContent id={id} setGmoBankAccount={setGmoBankAccount} />
    </Form>
  )
}
export const BankAccountCompleteForm: React.FC = () => {
  return (
    <Box sx={{ display: 'flex', justifyContent: 'center' }}>
      <Box sx={{ width: '1280px' }}>
        <Box sx={{ marginLeft: '0.5em' }}>
          <picture>
            <img src="/siteLogoR.svg" width="323px" alt="terashite R" />
          </picture>
        </Box>
        <Box
          sx={{
            backgroundColor: '#fff7e1',
            paddingTop: '2em',
            paddingBottom: '3em'
          }}>
          <Box sx={{ marginLeft: '0.5em', marginRight: '0.5em' }}>
            <Grid item xs={12} sx={{ marginTop: '2em' }}>
              <Grid container>
                <Grid item xs={12} display={'flex'} justifyContent={'center'}>
                  <Typography
                    variant="h5"
                    sx={{ marginTop: '1.5em', marginBottom: '1em' }}>
                    {'口座登録受付完了'}
                  </Typography>
                </Grid>
                <Grid
                  item
                  xs={12}
                  sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    mt: '-1em',
                    mb: '2em'
                  }}>
                  <picture>
                    <img src="/bar.png" alt="bar" />
                  </picture>
                </Grid>
              </Grid>
            </Grid>
            <Grid
              container
              justifyContent="center"
              spacing={2}
              sx={{
                marginTop: '0.1em'
              }}>
              <Grid
                item
                xs={12}
                sx={{ display: 'flex', justifyContent: 'center' }}>
                <Typography
                  variant="h6"
                  sx={{
                    marginTop: '0em',
                    whiteSpace: 'pre-wrap'
                  }}>
                  {`ご入力いただいた内容をもとに、口座登録を受け付けました。`}
                </Typography>
              </Grid>
            </Grid>
          </Box>
        </Box>
      </Box>
    </Box>
  )
}
